import React, { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import ClientSearchSelect from "~/pages/payments/components/ClientSearchSelect";
import {
  ClientSuggestion,
  SalesRequestSuggestion,
} from "~/pages/payments/hooks/useClientSuggestions";
import { UnidentifiedPayment } from "~/pages/payments/hooks/useUnidentifiedPayments";

interface PaymentAssignmentFormProps {
  payment: UnidentifiedPayment;
  clientSuggestions: ClientSuggestion[];
  requestSuggestions: SalesRequestSuggestion[];
  loading: boolean;
  onSubmit: (clientId: string, salesRequestId?: string) => void;
  onCancel: () => void;
}

export default function PaymentAssignmentForm({
  payment,
  clientSuggestions,
  requestSuggestions,
  loading,
  onSubmit,
  onCancel,
}: PaymentAssignmentFormProps) {
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedRequestId, setSelectedRequestId] = useState<string>("");

  // Reset request selection when client changes
  useEffect(() => {
    setSelectedRequestId("");
  }, [selectedClientId]);

  // Filter requests to only show those for the selected client
  const filteredRequests = selectedClientId
    ? requestSuggestions.filter((req) => req.clientId === selectedClientId)
    : [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(amount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedClientId) {
      onSubmit(selectedClientId, selectedRequestId || "none-selected");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <h3 className="font-semibold">Detalles del Pago</h3>
        <div className="rounded bg-muted p-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-sm font-medium text-muted-foreground">
                ID Transacción:
              </span>
              <p className="font-medium">
                {payment.txInternalId || `TX-${payment.id}`}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">
                Monto:
              </span>
              <p className="text-lg font-bold">
                {formatCurrency(payment.amount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="client">Cliente</Label>
        <ClientSearchSelect
          value={selectedClientId}
          onChange={setSelectedClientId}
          defaultSuggestions={clientSuggestions}
        />

        {clientSuggestions.length > 0 &&
          clientSuggestions[0].id === selectedClientId && (
            <p className="text-xs text-green-600">
              Cliente sugerido por coincidencia de monto
            </p>
          )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="salesRequest">Solicitud de Venta (opcional)</Label>
        {loading ? (
          <div className="flex h-10 items-center justify-center rounded-md border bg-background px-3">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">
              Cargando solicitudes...
            </span>
          </div>
        ) : (
          <Select
            value={selectedRequestId}
            onValueChange={setSelectedRequestId}
            disabled={!selectedClientId || filteredRequests.length === 0}
          >
            <SelectTrigger id="salesRequest" className="text-ellipsis">
              <SelectValue placeholder="Seleccionar solicitud de venta" />
            </SelectTrigger>
            <SelectContent className="max-w-[280px]">
              <SelectItem value="none-selected">Ninguna solicitud</SelectItem>
              {filteredRequests.map((request) => (
                <SelectItem
                  key={request.id}
                  value={request.id}
                  className="truncate"
                >
                  <div className="truncate">
                    {request.id.slice(0, 8)}... (
                    {formatCurrency(request.totalAmount)})
                    {request.status && (
                      <span className="ml-1 inline-block">
                        {request.status === "pending" && "• Pendiente"}
                        {request.status === "prepaid" && "• Esperando Pago"}
                        {request.status === "accepted" && "• Aceptada"}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {filteredRequests.length > 0 &&
          filteredRequests[0].id === selectedRequestId && (
            <p className="text-xs text-green-600">
              Solicitud sugerida por coincidencia de monto
            </p>
          )}

        {selectedClientId && filteredRequests.length === 0 && !loading && (
          <p className="text-xs text-amber-600">
            No hay solicitudes de venta pendientes para este cliente
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!selectedClientId}>
          Asignar Pago
        </Button>
      </div>
    </form>
  );
}
